import { Box } from '#/components/Box/Box';
import { ButtonOrLink } from '#/components/ButtonOrLink/ButtonOrLink';
import { Divider } from '#/components/Divider/Divider';
import { Hide } from '#/components/Hide/Hide';
import { ActionOppsIcon } from '#/components/Icon/ActionOpps/ActionOppsIcon';
import { Icon } from '#/components/Icon/Icon';
import { LegacyTertiaryButton } from '#/components/LegacyButton/LegacyTertiaryButton';
import { PrintHidden } from '#/components/PrintHidden/PrintHidden';
import { Body } from '#/components/Text/Body';
import { BodySmall } from '#/components/Text/BodySmall';
import { H2 } from '#/components/Text/H2';
import { H4 } from '#/components/Text/H4';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { EmbedSearchWidgetAlgoliaImportedVolopHit } from '#/modules/embedSearchWidget/volops/algolia/EmbedSearchWidgetAlgoliaImportedVolopHit';
import type { EmbedSearchWidghetApiImportedVolopType } from '#/modules/embedSearchWidget/volops/api/types/EmbedSearchWidghetApiImportedVolopType';
import { ListingTypeLabel } from '#/modules/listing/components/ListingTypeLabel/ListingTypeLabel';
import { ListingViewBody } from '#/modules/listing/components/ListingView/Body/ListingViewBody';
import { ListingViewMain } from '#/modules/listing/components/ListingView/Main/ListingViewMain';
import { ListingViewSidebar } from '#/modules/listing/components/ListingView/Sidebar/ListingViewSidebar';
import { SearchContextHeader } from '#/modules/search/components/Context/SearchContextHeader';
import { useSearchContextHeader } from '#/modules/search/containers/SearchContextHeader/useSearchContextHeader';
import { getCityStateString } from '#/utils/address/getCityStateString';
import { pathWithParams } from '#/utils/url/pathWithParams';

const NAME_BY_IMPORTED_TYPE = {
  GOLDEN: 'Golden',
} satisfies Record<EmbedSearchWidghetApiImportedVolopType, string>;

type Props = {
  importedVolopHit: EmbedSearchWidgetAlgoliaImportedVolopHit;
};

export function EmbedSearchWidgetViewImportedVolopPage({
  importedVolopHit,
}: Props) {
  const { i18n } = useI18n();

  const { searchContext } = useSearchContextHeader({
    currentObjectId: importedVolopHit.objectID,
  });

  return (
    <ListingViewBody>
      <ListingViewSidebar maxWidth={288}>
        <BodySmall lineHeight="normal">
          <PrintHidden>
            <Hide xs sm md>
              <Box mb={24}>
                <ActionOppsIcon
                  name={
                    importedVolopHit.source.toLowerCase() as Lowercase<EmbedSearchWidghetApiImportedVolopType>
                  }
                  width="60%"
                  maxWidth={180}
                  maxHeight={120}
                />
              </Box>
            </Hide>
            <Box mb={[4, 4, 4, '12px']}>
              <Divider marginTop={0} marginBottom={0} />
            </Box>
          </PrintHidden>
        </BodySmall>
      </ListingViewSidebar>

      <ListingViewMain>
        {searchContext && (
          <PrintHidden>
            <Box mb={[4, 4, 4, '40px']}>
              <SearchContextHeader searchContext={searchContext} />
            </Box>
          </PrintHidden>
        )}

        <Box mt={[0, 2, 2, 2]} mb={[2, 2, 2, 4]}>
          <ListingTypeLabel listingType="IMPORTED" orgType={null} />
        </Box>

        <H2
          fontSize={['30px', '30px', '30px', '36px']}
          color="mediumContentGrey"
          mb="24px"
          qaId="listing-name"
        >
          {importedVolopHit.name}
        </H2>

        <H4>
          {getCityStateString(i18n, {
            city: importedVolopHit.city,
            stateCode: importedVolopHit.state || importedVolopHit.stateStr,
            country: importedVolopHit.country,
          })}
        </H4>

        <Body>{importedVolopHit.description}</Body>

        <Box my={48} component={LegacyTertiaryButton}>
          <ButtonOrLink
            type="link"
            href={pathWithParams(importedVolopHit.url, {
              utm_source: 'idealist',
              utm_medium: 'referral',
              utm_campaign: 'action_opportunities',
            })}
            target="_blank"
          >
            <Box display="flex" alignItems="center">
              {i18n(`View on %(title)s`, {
                title: NAME_BY_IMPORTED_TYPE[importedVolopHit.source],
              })}
              <Box ml="6px">
                <Icon name="arrow-up-right" size={12} />
              </Box>
            </Box>
          </ButtonOrLink>
        </Box>

        {searchContext && (
          <PrintHidden>
            <Box mb={48}>
              <SearchContextHeader searchContext={searchContext} noDivider />
            </Box>
          </PrintHidden>
        )}
      </ListingViewMain>
    </ListingViewBody>
  );
}
